import { useEffect, useRef, useState } from 'react';
import { IconRefresh, IconX } from '@tabler/icons-react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import {
  createUpdateChecker,
  DISMISS_SNOOZE_MS,
  type UpdateChecker,
} from '../../lib/sw-update.js';

/**
 * "A new version is available" banner (Brief UPDATE-PROMPT).
 *
 * With registerType 'prompt', a freshly deployed service worker installs and
 * then WAITS; `needRefresh` flips true and this banner appears. Nothing happens
 * until the user clicks Reload: that messages SKIP_WAITING to the waiting
 * worker, and once it takes control the page does a plain soft reload — the
 * HttpOnly refresh cookie survives and bootSession() restores the login, so the
 * session is never lost. There is deliberately no automatic reload path and no
 * cache-busting hard reload (a hard reload logged users out — session 33).
 *
 * Dismiss only snoozes the banner (DISMISS_SNOOZE_MS); it reappears afterwards
 * while the update is still pending. Discovery runs on an interval plus on tab
 * focus via createUpdateChecker.
 */
export function UpdatePrompt() {
  const [snoozed, setSnoozed] = useState(false);
  const [reloading, setReloading] = useState(false);
  const snoozeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkerRef = useRef<UpdateChecker | null>(null);

  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      const checker = createUpdateChecker(() => {
        // A failed check (offline, server hiccup) is fine — the next one runs.
        registration.update().catch(() => undefined);
      });
      checker.start();
      checkerRef.current = checker;
    },
  });

  useEffect(() => {
    const onFocus = () => checkerRef.current?.handleFocus();
    const onVisibility = () => {
      if (!document.hidden) checkerRef.current?.handleFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      checkerRef.current?.stop();
      if (snoozeTimer.current) clearTimeout(snoozeTimer.current);
    };
  }, []);

  function dismiss() {
    setSnoozed(true);
    if (snoozeTimer.current) clearTimeout(snoozeTimer.current);
    snoozeTimer.current = setTimeout(() => setSnoozed(false), DISMISS_SNOOZE_MS);
  }

  function reload() {
    setReloading(true);
    // Sends SKIP_WAITING; the plugin soft-reloads once the new SW is controlling.
    void updateServiceWorker();
  }

  if (!needRefresh || snoozed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="surface flex w-full max-w-md items-center gap-3 px-4 py-3 shadow-pop">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-navy">
          <IconRefresh size={18} className="text-gold-soft" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ink-soft">
            <span className="font-medium text-ink">A new version of Admitly is available.</span>{' '}
            Reload when you're ready — you'll stay signed in.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={reloading}
          className="shrink-0 rounded-lg bg-navy px-3 py-2 text-sm font-medium text-cream hover:bg-navy-soft transition disabled:opacity-60"
        >
          {reloading ? 'Reloading…' : 'Reload'}
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss update prompt"
          className="shrink-0 flex h-9 w-9 items-center justify-center rounded-lg text-muted hover:bg-cream-deep/60 transition"
        >
          <IconX size={18} />
        </button>
      </div>
    </div>
  );
}
